import React, { useState } from 'react';
import { Target, ChevronDown, ChevronUp } from 'lucide-react';
import { useAiAccuracyReport } from '../hooks/useApi';
import type { AiAccuracyJobReport } from '../types';

// Format an ISO timestamp as Mountain Time: M/D/YYYY h:mm AM/PM MT
function formatMountainTime(isoString: string): string {
  try {
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      timeZone: 'America/Denver',
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }) + ' MT';
  } catch {
    return isoString;
  }
}

function scoreColor(score: number | null): string {
  if (score === null) return 'text-gray-400 dark:text-gray-500';
  if (score >= 3.5) return 'text-green-600 dark:text-green-400';
  if (score >= 2.5) return 'text-amber-600 dark:text-amber-400';
  return 'text-red-600 dark:text-red-400';
}

function formatPct(rate: number | null): string {
  if (rate === null) return '—';
  return `${Math.round(rate)}%`;
}

function formatScore(score: number | null): string {
  if (score === null) return '—';
  return score.toFixed(2);
}

const TOP_JOB_LIMIT = 10;

const AiAccuracyWidget: React.FC = () => {
  const [isOpen, setIsOpen] = useState(true);
  const { data, isLoading, error } = useAiAccuracyReport();

  const sortedJobs: AiAccuracyJobReport[] = data
    ? [...data.jobs].sort((a, b) => b.aiReviewedCount - a.aiReviewedCount)
    : [];

  const displayedJobs = sortedJobs.slice(0, TOP_JOB_LIMIT);
  const hasMore = sortedJobs.length > TOP_JOB_LIMIT;

  return (
    <div className="card dark:bg-gray-800 dark:border-gray-700">
      {/* Header */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
        aria-expanded={isOpen}
      >
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-500">
            <Target className="h-4 w-4 text-white" />
          </div>
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">
            AI Review Accuracy
          </h2>
          {data && (
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {data.summary.coveragePct.toFixed(0)}% coverage
            </span>
          )}
        </div>
        {isOpen ? (
          <ChevronUp className="h-5 w-5 text-gray-400 dark:text-gray-500" />
        ) : (
          <ChevronDown className="h-5 w-5 text-gray-400 dark:text-gray-500" />
        )}
      </button>

      {isOpen && (
        <div className="p-6 space-y-6">
          {/* Freshness timestamp */}
          {data && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Data fresh as of: {formatMountainTime(data.generatedAt)}
            </p>
          )}

          {isLoading && (
            <div className="flex h-32 items-center justify-center text-gray-500 dark:text-gray-400">
              Loading AI accuracy report...
            </div>
          )}

          {error && (
            <div className="flex h-32 items-center justify-center text-red-500 dark:text-red-400 text-sm">
              Failed to load accuracy report. The report may not be generated yet.
            </div>
          )}

          {data && (
            <>
              {/* Summary stats row */}
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-3">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Coverage</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                    {data.summary.coveragePct.toFixed(0)}%
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {data.summary.totalAiReviewed} / {data.summary.totalApplications}
                  </p>
                </div>

                <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-3">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Advanced</p>
                  <p className="text-xl font-bold text-green-600 dark:text-green-400">
                    {data.summary.advancedCount}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">moved forward</p>
                </div>

                <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-3">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Rejected</p>
                  <p className="text-xl font-bold text-red-600 dark:text-red-400">
                    {data.summary.rejectedCount}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">declined</p>
                </div>

                <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-3">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Hired</p>
                  <p className="text-xl font-bold text-indigo-600 dark:text-indigo-400">
                    {data.summary.hiredCount}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">placed</p>
                </div>

                <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-3">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Override Rate</p>
                  <p className="text-xl font-bold text-amber-600 dark:text-amber-400">
                    {formatPct(data.summary.humanOverrideRate)}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {data.summary.humanOverrideCount} / {data.summary.humanReviewedCount}
                  </p>
                </div>
              </div>

              {/* Per-job compact table */}
              {displayedJobs.length > 0 && (
                <div>
                  <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                          <th className="text-left px-4 py-2 font-medium text-gray-600 dark:text-gray-400">Job</th>
                          <th className="text-right px-3 py-2 font-medium text-gray-600 dark:text-gray-400">AI Reviewed</th>
                          <th className="text-right px-3 py-2 font-medium text-gray-600 dark:text-gray-400">Avg Score</th>
                          <th className="text-right px-3 py-2 font-medium text-gray-600 dark:text-gray-400 hidden sm:table-cell">High→Adv%</th>
                          <th className="text-right px-3 py-2 font-medium text-gray-600 dark:text-gray-400 hidden sm:table-cell">Low→Rej%</th>
                          <th className="text-right px-3 py-2 font-medium text-gray-600 dark:text-gray-400 hidden md:table-cell">Overrides</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {displayedJobs.map((job) => (
                          <tr
                            key={job.jobId}
                            className="bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                          >
                            <td className="px-4 py-2 text-gray-900 dark:text-gray-100 truncate max-w-[180px]" title={job.jobTitle}>
                              {job.jobTitle}
                            </td>
                            <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">
                              {job.aiReviewedCount}
                              <span className="text-gray-400 dark:text-gray-500 text-xs ml-1">
                                ({job.aiReviewedPct.toFixed(0)}%)
                              </span>
                            </td>
                            <td className={`px-3 py-2 text-right font-medium ${scoreColor(job.avgAiScore)}`}>
                              {formatScore(job.avgAiScore)}
                            </td>
                            <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300 hidden sm:table-cell">
                              {formatPct(job.accuracy.highScoreAdvanceRate)}
                            </td>
                            <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300 hidden sm:table-cell">
                              {formatPct(job.accuracy.lowScoreRejectRate)}
                            </td>
                            <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300 hidden md:table-cell">
                              {job.humanComparison.humanOverrideCount}
                              {job.humanComparison.humanReviewedCount > 0 && (
                                <span className="text-gray-400 dark:text-gray-500 text-xs ml-1">
                                  / {job.humanComparison.humanReviewedCount}
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {hasMore && (
                    <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 text-right">
                      Showing top {TOP_JOB_LIMIT} of {sortedJobs.length} jobs (by AI reviews)
                    </p>
                  )}
                </div>
              )}

              {displayedJobs.length === 0 && (
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                  No job accuracy data available yet.
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

AiAccuracyWidget.displayName = 'AiAccuracyWidget';

export default AiAccuracyWidget;
